import { DealerContactType, DealerRelatedUser, UpdateDealerRequest } from '@api/types';
import { useAppDispatch, useAuth } from '@hooks';
import { getDealer, getDealerPublicAddress } from '@store/dealer/dealer.slice';
import { setModalState } from '@store/modal/modal.slice';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

type DealerDetailsContextProps = {
  children: React.ReactNode;
};

type DealerDetailsContextType = {
  isAuth: boolean;
  isBerema: boolean;
  canEditData: boolean;
  form: UseFormReturn<UpdateDealerRequest>;
  toggleEditDataState: (arg: boolean) => void;
  resetForm: () => void;
  contacts: {
    contactType: DealerContactType;
    contactValue: string;
  }[];
  setDeleteUserEmail: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  deleteUserEmail: string | null | undefined;
  addUser: (user: DealerRelatedUser) => void;
  deleteUser: () => void;
  users: DealerRelatedUser[];
  createModalId: string;
  deleteModalId: string;
};

export const DealerDetailsContext = createContext<DealerDetailsContextType | null>(null);

export const DealerDetailsProvider: React.FC<DealerDetailsContextProps> = ({ children }) => {
  const { isDealerUser, isBeremaUser, customerNo, isOEMUser, isServiceUser } = useAuth();
  const isCustomer = isDealerUser || isOEMUser || isServiceUser;
  const dealer = useSelector(getDealer);
  const dispatch = useAppDispatch();
  const createModalId = 'create-user-modal';
  const deleteModalId = 'delete-user-modal';
  const [deleteUserEmail, setDeleteUserEmail] = useState<string | null | undefined>(null);
  const [users, setUsers] = useState<DealerRelatedUser[]>(dealer.relatedUsers || []);
  const isAuth = isBeremaUser || (isCustomer && dealer.customerNo === customerNo) || false;
  const isBerema = isBeremaUser || false;
  const [canEditData, setCanEditData] = useState(false);
  const dealerPublicAddress = useSelector(getDealerPublicAddress);
  const contacts: UpdateDealerRequest['contacts'] = useMemo(
    () => [
      {
        contactType: 'MobilePhoneNumber',
        contactValue:
          dealer?.contacts?.find((contact) => contact.contactType === 'MobilePhoneNumber')?.value ??
          '',
      },
      {
        contactType: 'ContactEmail',
        contactValue:
          dealer?.contacts?.find((contact) => contact.contactType === 'ContactEmail')?.value ?? '',
      },
    ],
    [dealer?.contacts]
  );

  const addUser = useCallback(
    (user: DealerRelatedUser) => {
      setUsers([...users, user]);
    },
    [users]
  );

  const deleteUser = useCallback(() => {
    setUsers(users.filter((user) => user.userEmail !== deleteUserEmail));
    dispatch(setModalState({ id: deleteModalId, isVisible: false }));
  }, [users, deleteUserEmail, dispatch, deleteModalId]);

  const formState = useMemo(
    () => ({
      shopAlternativeName: dealerPublicAddress?.alternativeName ?? '',
      workingHours: dealerPublicAddress?.workingHours ?? '',
      shopName: dealerPublicAddress?.name ?? '',
      organizationId: dealer?.id ?? '',
      addressId: dealerPublicAddress?.addressId ?? '',
      city: dealerPublicAddress?.city ?? '',
      zipCode: dealerPublicAddress?.postalCode ?? '',
      addressLine1: dealerPublicAddress?.line1 ?? '',
      addressLine2: dealerPublicAddress?.line2 ?? '',
      addressType: dealerPublicAddress?.addressType ?? '',
      countryName: dealerPublicAddress?.countryName ?? '',
      countryCode: dealerPublicAddress?.countryCode ?? '',
      webPage: dealerPublicAddress?.webPage ?? '',
      contacts,
    }),
    [
      contacts,
      dealer?.id,
      dealerPublicAddress?.addressId,
      dealerPublicAddress?.addressType,
      dealerPublicAddress?.alternativeName,
      dealerPublicAddress?.city,
      dealerPublicAddress?.countryCode,
      dealerPublicAddress?.countryName,
      dealerPublicAddress?.line1,
      dealerPublicAddress?.line2,
      dealerPublicAddress?.name,
      dealerPublicAddress?.postalCode,
      dealerPublicAddress?.workingHours,
      dealerPublicAddress?.webPage,
    ]
  );
  const form = useForm<UpdateDealerRequest>({
    defaultValues: {
      ...formState,
    },
  });
  const toggleEditDataState = useCallback(
    (arg: boolean) => {
      setCanEditData(arg);
      form.reset(formState);
    },
    [form, formState]
  );

  const resetForm = useCallback(() => {
    form.reset(formState);
  }, [form, formState]);

  const contextValue: DealerDetailsContextType = useMemo(() => {
    return {
      isAuth,
      canEditData,
      resetForm,
      form,
      toggleEditDataState,
      contacts,
      setDeleteUserEmail,
      deleteUserEmail,
      addUser,
      deleteUser,
      users,
      createModalId,
      deleteModalId,
      isBerema,
    };
  }, [
    isAuth,
    canEditData,
    resetForm,
    form,
    toggleEditDataState,
    contacts,
    deleteUserEmail,
    setDeleteUserEmail,
    addUser,
    deleteUser,
    users,
    isBerema,
  ]);

  return (
    <DealerDetailsContext.Provider value={contextValue}>{children}</DealerDetailsContext.Provider>
  );
};

export const useDealerDetailsContext = () => {
  const context = useContext(DealerDetailsContext);

  if (!context) {
    throw new Error('useDealerDetailsContext can only be used inside DealerDetailsContextProvider');
  }

  return context;
};
